import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AgGridAngular } from 'ag-grid-angular';
import { ActivatedRoute } from '@angular/router';
import { ScannersService } from '../../@services/scanners.service';
import { CustomerDirectoryService } from '../../@services/customer-directory.service';
import { UserPreferencesService, PinnedScanner } from '../../@services/user-preferences.service';
import { ToastService } from '../../@services/toast.service';
import { agGridThemes } from '../../@shared/ag-grid-themes';
import * as _ from 'lodash';
import { StreamEditorComponent, StreamEntry } from '../shared/stream-editor/stream-editor.component';
import { ChannelsCatalogEditorComponent, ChannelDef } from '../shared/channels-catalog-editor/channels-catalog-editor.component';
@Component({
  selector: 'app-scanners',
  imports: [AgGridAngular, CommonModule, FormsModule, StreamEditorComponent, ChannelsCatalogEditorComponent],
  templateUrl: './scanners.component.html',
  styleUrl: './scanners.component.scss'
})
export class ScannersComponent implements OnInit {

  subs: any = [];
  theme: any;
  gridDarkTheme = agGridThemes.dark;
  grid: any;
  scanners: any[] = [];
  filtered_scaners: any[] = [];
  selected_scanner: any;
  // Set to true after the first getScanners() round-trip completes
  // (success or err). Gates the Add Scanner button so admins can't open the
  // modal before the auto-prefix derivation has data to work with.
  scanners_loaded: boolean = false;

  showAddScannerModal = false;
  newScanner: any = {
    Id: '',
    Name: '',
    Client: '',
    Url: '',
    AppUrl: '',
    RoleAccess: 'member',
  }

  showDeleteScannerModal = false;

  // Edit modal state
  showEditScannerModal = false;
  editScanner: any = { Id: '', Name: '', Client: '', Url: '', AppUrl: '', RoleAccess: 'member' };
  // Stringified JSON for the structured fields, kept separate from the
  // editScanner shape so the user can edit raw JSON and we parse it on
  // save (with an error toast on bad JSON). The read-only Verified
  // sub-object is rendered inline from selected_scanner.verified.
  editDataSourceJson = '';
  // IT-F2-406 cid a99fdac3 — Stream[] is now driven by the structured
  // stream-editor component. editStreamArray carries the live-edit state
  // between modal-open and save; the raw-JSON textarea retired.
  // IT-F2-406 cid 29bbc2d8 (c/12c4aa7f 2026-10-18) — the editor was moved
  // from inline to its own popup; showStreamEditor gates the overlay.
  editStreamArray: StreamEntry[] | null = null;
  showStreamEditor: boolean = false;
  // IT-F2-406 cid 9c3a4daf — Scanners.<id>.channels[] now has a structured
  // popup editor (same pattern as Stream[]) with per-channel role-visibility
  // chips. Opens from the Scanner edit modal.
  editChannelsArray: ChannelDef[] | null = null;
  showChannelsCatalogEditor: boolean = false;
  editExcludeTldsJson = '';
  // Comma-separated slug aliases (Scanners.<id>.aliases[]). Portal-mint
  // slugs a customer's SSO integration may send instead of the canonical
  // _id — the /validate-*-token endpoints resolve these via
  // get_scanner_by_id_or_alias. Stored lowercased.
  // IT-F2-459 Mike 2026-11-02: same field now also drives URL-routing on
  // branded hosts (/scans/<alias> → canonical scanner id). UX upgraded
  // to a tags-style input via editAliasInput + list helpers below;
  // editAliasesCsv stays as the serializer source of truth so the
  // existing save path (scanners.component.ts:728-733) is untouched.
  editAliasesCsv = '';
  editAliasInput = '';
  newAliasInput = '';

  // IT-F2-459 Mike 2026-11-02 — tags-input helpers. Chip list is derived
  // from the backing CSV; add/remove mutates the CSV. Lowercases + dedups
  // at commit time so the chip view matches what we'll save.
  _alias_list_from_csv(csv: string): string[] {
    return String(csv || '')
      .split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
  }
  _alias_csv_from_list(list: string[]): string {
    return Array.from(new Set(list)).join(', ');
  }
  get editAliasesList(): string[] { return this._alias_list_from_csv(this.editAliasesCsv); }
  get newAliasesList(): string[] { return this._alias_list_from_csv(this.newScanner.aliasesCsv); }
  add_edit_alias(raw: string) {
    const v = String(raw || '').trim().toLowerCase();
    if (!v) return;
    const list = this.editAliasesList;
    if (list.includes(v)) { this.editAliasInput = ''; return; }
    list.push(v);
    this.editAliasesCsv = this._alias_csv_from_list(list);
    this.editAliasInput = '';
  }
  remove_edit_alias(idx: number) {
    const list = this.editAliasesList;
    list.splice(idx, 1);
    this.editAliasesCsv = this._alias_csv_from_list(list);
  }
  add_new_alias(raw: string) {
    const v = String(raw || '').trim().toLowerCase();
    if (!v) return;
    const list = this.newAliasesList;
    if (list.includes(v)) { this.newAliasInput = ''; return; }
    list.push(v);
    this.newScanner.aliasesCsv = this._alias_csv_from_list(list);
    this.newAliasInput = '';
  }
  remove_new_alias(idx: number) {
    const list = this.newAliasesList;
    list.splice(idx, 1);
    this.newScanner.aliasesCsv = this._alias_csv_from_list(list);
  }
  // Shared key handler — Enter / comma commits the current input; Backspace
  // on empty input pops the last chip. Used by both Edit and Create forms.
  on_alias_keydown(ev: KeyboardEvent, mode: 'edit' | 'new') {
    const key = ev.key;
    if (key === 'Enter' || key === ',') {
      ev.preventDefault();
      if (mode === 'edit') this.add_edit_alias(this.editAliasInput);
      else this.add_new_alias(this.newAliasInput);
    } else if (key === 'Backspace') {
      if (mode === 'edit' && !this.editAliasInput) {
        const list = this.editAliasesList;
        if (list.length) { list.pop(); this.editAliasesCsv = this._alias_csv_from_list(list); }
      } else if (mode === 'new' && !this.newAliasInput) {
        const list = this.newAliasesList;
        if (list.length) { list.pop(); this.newScanner.aliasesCsv = this._alias_csv_from_list(list); }
      }
    }
  }
  editJsonError: string | null = null;
  // GithubCollab (IT-F2-178) — structured inputs for customer-collab
  // metadata. Mirror the sub-doc shape so ngModel binding is straightforward.
  // editGithubCollabEnabled toggles whether the sub-doc gets written on save;
  // false → payload.GithubCollab = null → backend $unsets.
  editGithubCollabEnabled = false;
  editGithubCollab: {
    collab: { owner: string; repo: string };
    private: { owner: string; repo: string };
    app: { slug: string; app_id: number | null; client_id: string };
    workflows: { sync: string; notify: string };
  } = {
    collab: { owner: '', repo: '' },
    private: { owner: '', repo: '' },
    app: { slug: '', app_id: null, client_id: '' },
    workflows: { sync: '', notify: '' },
  };
  // customer_collaborators as textarea, one handle per line. Optional role
  // suffix after ':' (e.g. `someuser:write`). Parsed on save into the
  // {handle, role, added_at} object shape.
  editCollaboratorsText = '';

  // RoleAccess dropdown options — mirrors backend ScannersService.VALID_ROLE_ACCESS.
  roleAccessOptions = ['disabled', 'admin', 'client', 'member'];

  // Customer directory — source-of-truth for the Client dropdown in
  // both Add and Edit scanner modals. Loaded on init alongside scanners.
  customers: any[] = [];

  // Inline "+ New Customer" mini-modal launched from the Client dropdown
  // in either scanner modal. After saving, the new customer is selected
  // automatically in whichever scanner modal launched it.
  showAddCustomerModal = false;
  newCustomer: any = { _id: '', name: '' };
  // 'add' | 'edit' — which scanner modal triggered the customer add,
  // so we can write the new slug back into the right form on save.
  addCustomerOrigin: 'add' | 'edit' | null = null;

  constructor(
    private scannersService: ScannersService,
    private customerDirectoryService: CustomerDirectoryService,
    private route: ActivatedRoute,
    private userPreferences: UserPreferencesService,
    private toastService: ToastService,
  ) {
    this.init_grid();
    this.theme = this.gridDarkTheme;
  }

  /** Row helper: true if this scanner's Id matches a pinned entry's key. */
  is_scanner_pinned(scanner: any): boolean {
    const id = String(scanner?.Id || '').toLowerCase();
    if (!id) return false;
    return this.userPreferences.pinnedScanners().some(p => p.key === id);
  }

  /**
   * Toggle-pin action from the grid row. Adds/removes a PinnedScanner
   * whose key matches the scanner's Id. Prefers AppUrl (the operator-
   * facing SPA URL) for the pin URL, falls back to Url if AppUrl is
   * empty. After the save, refresh the pin column so the icon updates
   * without a full grid re-render.
   */
  async toggle_pin_for_scanner(scanner: any): Promise<void> {
    if (!scanner) return;
    const id = String(scanner.Id || '').toLowerCase().trim();
    const label = String(scanner.Name || '').trim() || id;
    const url = String(scanner.AppUrl || scanner.Url || '').trim();
    if (!id) { this.toastService.error('Pin failed', 'Scanner has no Id.'); return; }
    if (!url) { this.toastService.error('Pin failed', 'Scanner has no AppUrl or Url — nothing to iframe.'); return; }
    const current = this.userPreferences.pinnedScanners();
    const alreadyPinned = current.some(p => p.key === id);
    const next: PinnedScanner[] = alreadyPinned
      ? current.filter(p => p.key !== id)
      : [...current, { key: id, label, url }];
    const ok = await this.userPreferences.savePinnedScanners(next);
    if (!ok) return;
    this.toastService.success(alreadyPinned ? 'Unpinned' : 'Pinned', `${label} — ${alreadyPinned ? 'removed from sidebar' : 'added to sidebar'}.`);
    // Refresh the pin column cell to flip the icon state; other columns
    // untouched. Cheap even at 5-figure row counts.
    try { this.grid?.api?.refreshCells({ columns: ['pin_action'], force: true }); } catch { /* ignore */ }
  }

  // Inbound query-param filter (e.g. /admin/scanners?client=option-pit
  // from the Customers page "# Scanners" link). When set, the grid
  // applies a Client filter on grid-ready.
  pendingClientFilter: string | null = null;

  ngOnInit(): void {
    // Read the query param once at mount. Applied in on_grid_ready when
    // the grid api becomes available.
    this.pendingClientFilter = this.route.snapshot.queryParamMap.get('client');
    this.get_scanners();
    this.get_customers();
  }

  apply_client_filter(client: string) {
    if (!this.grid?.api) return;
    // ag-grid v33 Enterprise: defaultColDef.filter:true resolves to
    // agSetColumnFilter (NOT text). Client column now sets filter:
    // 'agTextColumnFilter' explicitly so this model shape applies, and
    // we use setColumnFilterModel (Promise) instead of setFilterModel —
    // the latter silently dropped the model in our config.
    Promise.resolve(
      this.grid.api.setColumnFilterModel('Client', {
        filterType: 'text', type: 'equals', filter: client,
      })
    )
      .then(() => this.grid.api.onFilterChanged())
      .catch((e: any) => console.warn('apply_client_filter failed', e));
  }

  async get_customers() {
    this.customers = await this.customerDirectoryService.list();
  }

  async get_scanners() {
    this.scanners = [];
    this.selected_scanner = null;

    let res = await this.scannersService.getScanners();
    if (res && !res.err) {
      this.scanners = res;
    }

    // Flag the first round-trip as complete (regardless of err) so the
    // Add Scanner button is no longer locked out on transient Mongo blips.
    this.scanners_loaded = true;
    // Filter from a deep-link (?client=<slug>) is honored both at grid-
    // ready AND after data lands. The filter model can be set on an
    // empty grid but doesn't visibly filter until rows exist; re-applying
    // here forces the recompute now that rowData is populated.
    if (this.pendingClientFilter && this.grid?.api) {
      this.apply_client_filter(this.pendingClientFilter);
    }
    console.log('Scanners:', this.scanners);
  }

  /**** Scanners Grid ****/
  init_grid() {
  
    this.grid = {
      options: {
        context: this,
        suppressCellFocus: true,
        rowSelection: {
          mode: 'singleRow',
          checkboxes: false,
          enableClickSelection: true,
        },
        defaultColDef: {
          floatingFilter: true,
          // filter: 'agTextColumnFilter',
          filter: true,
          cellStyle: { 'text-align': 'left'},
        },
        getRowId: (params: any) => {
          return params.data.Id;
        },
        onRowClicked: this.on_row_clicked.bind(this),
      },
      statusBar: {
        statusPanels: [
          { statusPanel: 'agTotalRowCountComponent', align: 'left' },
          { statusPanel: 'agFilteredRowCountComponent', align: 'left' },
        ]
      },
      columnDefs: [
        {
          // Row action: pencil → open the right-side edit drawer for
          // this row. Selects the row + invokes the same handler the
          // toolbar's Edit button uses so state stays consistent.
          colId: 'actions',
          headerName: '',
          width: 56,
          suppressSizeToFit: true,
          filter: false,
          floatingFilter: false,
          sortable: false,
          resizable: false,
          pinned: 'left',
          cellRenderer: (_p: any) => `<button class="text-blue-400 hover:text-blue-300" title="Edit scanner"><i class="fa-solid fa-pen"></i></button>`,
          onCellClicked: (params: any) => {
            const ctx = params.context;
            ctx.selected_scanner = params.data;
            if (params.node && params.api) params.node.setSelected(true);
            ctx.on_edit_scanner_click();
          },
        },
        {
          // Pin toggle — sits right after the edit pencil. Gold thumbtack
          // when pinned, gray when not. Same on-cell-click handler as
          // Edit but calls toggle_pin_for_scanner instead. (IT-F2-52
          // follow-up: Mike asked for a pin icon here so he can pin any
          // scanner directly from this table.)
          colId: 'pin_action',
          headerName: '',
          width: 44,
          suppressSizeToFit: true,
          filter: false,
          floatingFilter: false,
          sortable: false,
          resizable: false,
          pinned: 'left',
          cellRenderer: (params: any) => {
            const pinned = params?.context?.is_scanner_pinned?.(params.data);
            const color = pinned
              ? 'text-yellow-400 hover:text-yellow-300'
              : 'text-gray-500 hover:text-gray-300';
            const title = pinned ? 'Unpin from sidebar' : 'Pin to sidebar';
            return `<button class="${color}" title="${title}"><i class="fa-solid fa-thumbtack"></i></button>`;
          },
          onCellClicked: (params: any) => {
            const ctx = params.context;
            void ctx.toggle_pin_for_scanner(params.data);
          },
        },
        {
          field: "Name",
          width: 250,
          suppressSizeToFit: true,
          headerName: 'Name',
          editable: true,
          onCellValueChanged: this.on_scanner_edit.bind(this),
        },
        {
          field: "Client",
          headerName: 'Client',
          width: 120,
          suppressSizeToFit: true,
          // Explicit text filter: avoids the Enterprise default (set
          // filter) so ?client= deep-link can use the text-filter model.
          filter: 'agTextColumnFilter',
          editable: true,
          onCellValueChanged: this.on_scanner_edit.bind(this),
        },
        {
          field: "Id",
          headerName: 'Id',
          width: 200,
          suppressSizeToFit: true,
        },
        {
          field: "Url",
          headerName: 'Url',
          editable: true,
          onCellValueChanged: this.on_scanner_edit.bind(this),
        },
        {
          field: "AppUrl",
          headerName: 'App URL',
          editable: true,
          onCellValueChanged: this.on_scanner_edit.bind(this),
        },
        {
          field: "RoleAccess",
          headerName: 'Role Access',
          width: 130,
          suppressSizeToFit: true,
          editable: true,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: { values: ['disabled', 'admin', 'client', 'member'] },
          onCellValueChanged: this.on_scanner_edit.bind(this),
          cellRenderer: (params: any) => {
            // Colour tag matches semantic level — disabled is gray,
            // admin red (most restrictive), client amber, member green.
            const v = String(params.value || 'member').toLowerCase();
            const klass = v === 'disabled' ? 'bg-gray-700 text-gray-200'
                       : v === 'admin'    ? 'bg-red-900/60 text-red-200'
                       : v === 'client'   ? 'bg-amber-800/60 text-amber-200'
                       :                    'bg-green-900/60 text-green-200';
            return `<span class="inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wide ${klass}">${v}</span>`;
          },
        },
        {
          field: "verified",
          headerName: 'Verified',
          width: 110,
          suppressSizeToFit: true,
          filter: false,
          floatingFilter: false,
          cellRenderer: (params: any) => {
            // verified: { ok, status, checked_at, error? }
            // Renders fa-check-circle / fa-times-circle with status code
            // hover tooltip. Empty = never verified yet.
            const v = params.value;
            if (!v) {
              return `<span class="text-gray-500 text-xs">—</span>`;
            }
            if (v.ok) {
              return `<span title="HTTP ${v.status} — checked ${v.checked_at || ''}"><i class="fa-solid fa-circle-check text-green-500"></i> <span class="text-xs text-gray-400">${v.status}</span></span>`;
            }
            const reason = v.error ? `${v.status || '—'} ${v.error}` : `HTTP ${v.status || '—'}`;
            return `<span title="${reason} — checked ${v.checked_at || ''}"><i class="fa-solid fa-circle-xmark text-red-500"></i> <span class="text-xs text-gray-400">${v.status || '—'}</span></span>`;
          },
        },
        {
          // Notification Center opt-in. When true the scanner surfaces
          // in NC's picker + users viewer (notification-center-mu
          // .vercel.app). Persists as Scanners.<id>.notification_center
          // .enabled = boolean. Editable cell — checkbox toggles the
          // whole sub-doc via update_scanner. Mike 2026-07-02.
          colId: 'notification_center_enabled',
          headerName: 'Notifications',
          width: 130,
          suppressSizeToFit: true,
          editable: true,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: { values: ['enabled', 'disabled'] },
          valueGetter: (params: any) => (params?.data?.notification_center?.enabled ? 'enabled' : 'disabled'),
          onCellValueChanged: this.on_scanner_edit.bind(this),
          cellRenderer: (params: any) => {
            const on = !!params?.data?.notification_center?.enabled;
            const klass = on
              ? 'bg-purple-900/60 text-purple-200'
              : 'bg-gray-700 text-gray-400';
            const label = on ? 'enabled' : 'disabled';
            return `<span class="inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wide ${klass}">${label}</span>`;
          },
        }
      ]
    }
  }
  on_grid_ready(e: any) {
    this.grid.api = e.api;
    this.grid.colApi = e.colApi;
    this.grid.api.sizeColumnsToFit();
    // get_scanners() also calls apply_client_filter after rowData lands;
    // calling here covers the case where rowData arrived before the api
    // (Angular binding ordering varies).
    if (this.pendingClientFilter) {
      this.apply_client_filter(this.pendingClientFilter);
    }
  }
  // ag-grid v33 fires firstDataRendered once after rows actually render
  // — the canonical hook for "preload a filter that needs rowData".
  on_first_data_rendered(e: any) {
    if (this.pendingClientFilter) {
      this.apply_client_filter(this.pendingClientFilter);
    }
  }
  on_model_updated(e: any) {
    //Get the filtered rows from the grid
    if (this.grid?.api) {
      this.filtered_scaners = [];
      this.grid?.api.forEachNodeAfterFilter((node: any) => {
        this.filtered_scaners.push(node.data);
      });
    }
  }
  on_row_selected(e: any) {
    // console.log('on_row_selected', e);
    if (e?.node?.isSelected() === true) {
      this.selected_scanner = e.node.data;
    }
  }
  on_row_clicked(e: any) {
    if (e.event.target !== undefined) {
      let scanner = e.data;
      let action = e.event.target.getAttribute('data-button-action');
      switch (action) {
        default:
          break;
      }
    }
  }


  /**** Actions ****/
  async refresh() {
    await this.get_scanners();
  }

  // Probe every scanner URL via the backend and re-load the grid so the
  // updated `verified` sub-objects render. Long-running (~8s worst case).
  async verify_all() {
    const res = await this.scannersService.verifyAllScanners();
    if (res && !res.err) {
      await this.get_scanners();
      // Force the ag-grid to re-render with the new rowData.
      if (this.grid?.api) this.grid.api.setGridOption('rowData', this.scanners);
    }
  }

  // Distinct Client values from currently-loaded scanners, used to autocomplete
  // the Add-Scanner Client input. Free-text entry is still allowed for new
  // clients; this just surfaces existing ones to minimize typo'd duplicates.
  get available_clients(): string[] {
    const set = new Set<string>();
    for (const s of this.scanners) if (s?.Client) set.add(s.Client);
    return Array.from(set).sort();
  }

  on_add_scanner_click() {
    this.newScanner.Id = '';
    this.newScanner.Name = '';
    this.newScanner.Client = '';
    this.newScanner.Url = '';
    this.newScanner.AppUrl = '';
    this.newScanner.RoleAccess = 'member';
    this.newScanner.aliasesCsv = '';
    this.showAddScannerModal = true;
  }
  // Called when the admin picks/types a Client in the Add-Scanner modal.
  // Derives a sensible Id prefix from existing scanners with the same Client
  // (longest common prefix of their IDs) and pre-fills newScanner.Id so the
  // admin can append the unique suffix. For brand-new clients with no
  // existing scanners, leaves Id blank.
  on_client_change(value: string) {
    const ids = this.scanners
      .filter((s: any) => s?.Client === value && s?.Id)
      .map((s: any) => s.Id);
    if (ids.length === 0) {
      this.newScanner.Id = '';
      return;
    }
    const prefix = this._longest_common_prefix(ids);
    this.newScanner.Id = prefix || '';
  }
  _longest_common_prefix(strs: string[]): string {
    if (!strs.length) return '';
    let pref = strs[0];
    for (let i = 1; i < strs.length; i++) {
      while (strs[i].indexOf(pref) !== 0) {
        pref = pref.slice(0, -1);
        if (!pref) return '';
      }
    }
    return pref;
  }
  // True when newScanner.Id collides with an Id already in the loaded scanners.
  // Drives the inline error message + disables Save.
  id_already_exists(): boolean {
    if (!this.newScanner.Id) return false;
    return this.scanners.some((s: any) => s?.Id === this.newScanner.Id);
  }
  can_create_scanner() {
    // Client is required so the scanner surfaces in the dynamic customer
    // list that powers the Add-User Customer picker and Edit-Customers modal.
    if (!this.newScanner.Id || !this.newScanner.Name || !this.newScanner.Client) {
      return false;
    }
    // Block save if the Id is already taken by another scanner.
    if (this.id_already_exists()) return false;
    return true;
  }
  async on_create_scanner() {
    // Materialize aliases from the CSV input just before send. Same lower
    // + dedupe pass the edit path applies so the backend gets a canonical
    // list. Absent / empty → omit the field entirely (no aliases).
    const _rawAliases = String(this.newScanner.aliasesCsv || '').split(',').map((s: string) => s.trim().toLowerCase()).filter(Boolean);
    const payload: any = { ...this.newScanner };
    delete payload.aliasesCsv;
    if (_rawAliases.length) payload.aliases = Array.from(new Set(_rawAliases));
    let res = await this.scannersService.createScanner(payload);
    if (res && !res.err) {
      //Add scanner to grid
      this.scanners.push(res);
      this.grid.api.setGridOption("rowData", this.scanners);
      this.showAddScannerModal = false;
    }
  }

  can_delete_scanner() {
    if (!this.selected_scanner) {
      return false;
    }
    return true;
  }

  can_edit_scanner() {
    return !!this.selected_scanner;
  }
  on_edit_scanner_click() {
    if (!this.selected_scanner) return;
    // Deep-copy so cancel doesn't mutate the grid row.
    this.editScanner = {
      Id: this.selected_scanner.Id,
      Name: this.selected_scanner.Name || '',
      // ShortName pre-fill mirrors the new-scanner form field — optional
      // compact label used as the commerce welcome email subject prefix
      // (`[ShortName] Your Scanner Login Credentials`). Falls back to
      // Name on the backend when blank.
      ShortName: this.selected_scanner.ShortName || '',
      Client: this.selected_scanner.Client || '',
      Url: this.selected_scanner.Url || '',
      AppUrl: this.selected_scanner.AppUrl || '',
      RoleAccess: this.selected_scanner.RoleAccess || 'member',
    };
    // Pre-fill JSON textareas from existing structured fields. Empty
    // string means "no override" — on save, blank JSON is treated as
    // "unset / drop the field" so admins can clear without typing null.
    this.editDataSourceJson = this.selected_scanner.DataSource
      ? JSON.stringify(this.selected_scanner.DataSource, null, 2) : '';
    // IT-F2-406 cid a99fdac3 — Stream[] now a structured editor. Normalize
    // to array on load (backend stores single-entry scanners as a bare
    // object sometimes); stream-editor emits a tidy array back on save.
    const _s = this.selected_scanner.Stream;
    this.editStreamArray = Array.isArray(_s)
      ? [..._s]
      : (_s && typeof _s === 'object') ? [_s] : null;
    // IT-F2-406 cid 9c3a4daf — channels[] catalog editor state. The NC
    // settings fields (enabled / body_html_template / schedule / data_source)
    // live inline on each ChannelDef as of Mike clar a712d669 — no sibling
    // state needed anymore. Legacy channel_templates on the scanner doc is
    // migrated in-place by the channels-catalog-editor's _load().
    const _ch = this.selected_scanner.channels;
    const _ct = this.selected_scanner.channel_templates;
    const liveChannels: ChannelDef[] = Array.isArray(_ch) ? _ch.map((c: any) => ({ ...c })) : [];
    // Hoist any lingering channel_templates.<id> fields onto the matching
    // channel entry so admins editing a scanner that was last written under
    // the old sibling-map scheme see the NC settings pre-filled. Keys that
    // don't match a channel entry are synthesised as new channels[] entries
    // (title derived from the key) so they're not silently lost on save.
    if (_ct && typeof _ct === 'object' && !Array.isArray(_ct)) {
      const byId = new Map<string, ChannelDef>();
      for (const c of liveChannels) if (c.id) byId.set(c.id, c);
      for (const k of Object.keys(_ct)) {
        const tpl = (_ct as any)[k] || {};
        const existing = byId.get(k);
        const target = existing || (() => {
          const title = String(k || '').split(/[_\-\s]+/).filter(Boolean)
            .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ') || k;
          const synth: ChannelDef = { id: k, title };
          liveChannels.push(synth);
          byId.set(k, synth);
          return synth;
        })();
        if (target.enabled === undefined && tpl.enabled !== undefined) target.enabled = !!tpl.enabled;
        if (!target.body_html_template && tpl.body_html_template) target.body_html_template = tpl.body_html_template;
        if (!target.schedule && tpl.schedule && (tpl.schedule.presets?.length || tpl.schedule.cron)) {
          target.schedule = {
            presets: Array.isArray(tpl.schedule.presets) ? [...tpl.schedule.presets] : [],
            cron: tpl.schedule.cron || '',
          };
        }
      }
    }
    this.editChannelsArray = liveChannels.length ? liveChannels : null;
    this.editExcludeTldsJson = Array.isArray(this.selected_scanner.ExcludeTlds) && this.selected_scanner.ExcludeTlds.length
      ? JSON.stringify(this.selected_scanner.ExcludeTlds) : '';
    // Prefill aliases as comma-separated for editing. Empty = no aliases.
    this.editAliasesCsv = Array.isArray(this.selected_scanner.aliases) && this.selected_scanner.aliases.length
      ? this.selected_scanner.aliases.join(', ') : '';
    // GithubCollab (IT-F2-178) — prefill panel from selected_scanner.GithubCollab.
    // Absent sub-doc → panel disabled (checkbox off, inputs blank).
    const _gc = this.selected_scanner.GithubCollab;
    if (_gc && typeof _gc === 'object') {
      this.editGithubCollabEnabled = true;
      this.editGithubCollab = {
        collab: { owner: _gc.collab?.owner || '', repo: _gc.collab?.repo || '' },
        private: { owner: _gc.private?.owner || '', repo: _gc.private?.repo || '' },
        app: {
          slug: _gc.app?.slug || '',
          app_id: typeof _gc.app?.app_id === 'number' ? _gc.app.app_id : null,
          client_id: _gc.app?.client_id || '',
        },
        workflows: {
          sync: _gc.workflows?.sync || '',
          notify: _gc.workflows?.notify || '',
        },
      };
      this.editCollaboratorsText = Array.isArray(_gc.customer_collaborators)
        ? _gc.customer_collaborators
            .map((c: any) => c?.role ? `${c.handle}:${c.role}` : String(c?.handle || ''))
            .filter(Boolean)
            .join('\n')
        : '';
    } else {
      this.editGithubCollabEnabled = false;
      this.editGithubCollab = {
        collab: { owner: '', repo: '' },
        private: { owner: '', repo: '' },
        app: { slug: '', app_id: null, client_id: '' },
        workflows: { sync: '', notify: '' },
      };
      this.editCollaboratorsText = '';
    }
    this.editJsonError = null;
    this.showEditScannerModal = true;
    // Reset the Stream editor overlay state — if the admin had it open
    // when they cancelled the previous scanner edit, we don't want it to
    // re-pop automatically on the next scanner.
    this.showStreamEditor = false;
    this.showChannelsCatalogEditor = false;
  }
  can_save_edit() {
    return !!this.editScanner.Id && !!this.editScanner.Name && !!this.editScanner.Client;
  }
  // Inline "+ New Customer" handlers — launched from a "+" button next
  // to the Client dropdown in either scanner modal. Saves to Customers
  // via the directory service, then writes the slug back into whichever
  // scanner form opened it.
  open_add_customer(origin: 'add' | 'edit') {
    this.addCustomerOrigin = origin;
    this.newCustomer = { _id: '', name: '' };
    this.showAddCustomerModal = true;
  }
  can_save_new_customer(): boolean {
    const slug = (this.newCustomer._id || '').trim();
    const name = (this.newCustomer.name || '').trim();
    if (!slug || !name) return false;
    if (this.customers.some((c: any) => c._id === slug)) return false;
    return true;
  }
  async on_save_new_customer() {
    if (!this.can_save_new_customer()) return;
    const payload = {
      _id: (this.newCustomer._id || '').trim(),
      name: (this.newCustomer.name || '').trim(),
      status: 'active',
      domains: [],
      customer_owned_domain: false,
    };
    const res = await this.customerDirectoryService.create(payload);
    if (res && !res.err) {
      // Refresh the list, then preselect the new slug in whichever
      // scanner form launched the add.
      await this.get_customers();
      if (this.addCustomerOrigin === 'edit') {
        this.editScanner.Client = payload._id;
      } else if (this.addCustomerOrigin === 'add') {
        this.newScanner.Client = payload._id;
        this.on_client_change(payload._id);
      }
      this.addCustomerOrigin = null;
      this.showAddCustomerModal = false;
    }
  }

  openStreamEditor() {
    this.showStreamEditor = true;
  }

  openChannelsCatalogEditor() {
    this.showChannelsCatalogEditor = true;
  }

  async on_save_edit() {
    if (!this.can_save_edit()) return;
    // Parse the JSON textareas. Empty input = field removed (backend
    // unsets it). Bad JSON = inline error message, save aborted.
    const payload: any = { ...this.editScanner };
    try {
      payload.DataSource = this.editDataSourceJson.trim()
        ? JSON.parse(this.editDataSourceJson) : null;
      // IT-F2-406 cid a99fdac3 — Stream[] now carries the editStreamArray
      // state from the structured editor. Null = backend $unsets.
      payload.Stream = (this.editStreamArray && this.editStreamArray.length)
        ? this.editStreamArray : null;
      // IT-F2-406 cid 9c3a4daf — channels[] from the catalog editor.
      // Null = backend $unsets.
      payload.channels = (this.editChannelsArray && this.editChannelsArray.length)
        ? this.editChannelsArray : null;
      payload.ExcludeTlds = this.editExcludeTldsJson.trim()
        ? JSON.parse(this.editExcludeTldsJson) : null;
      // Aliases: comma-separated → lowercase-trimmed unique array.
      // Empty → null so the backend $unsets the field entirely (absence
      // is the canonical "no aliases" state — mirrors the ExcludeTlds
      // / DataSource unset semantics).
      const _rawAliases = (this.editAliasesCsv || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
      payload.aliases = _rawAliases.length ? Array.from(new Set(_rawAliases)) : null;
      // GithubCollab (IT-F2-178). Panel disabled → null (backend $unsets).
      // Panel enabled → build object; validate the 4 required fields client-
      // side so we don't round-trip to the backend for an obvious error.
      if (this.editGithubCollabEnabled) {
        const gc = this.editGithubCollab;
        const missing: string[] = [];
        if (!gc.collab.owner.trim()) missing.push('collab.owner');
        if (!gc.collab.repo.trim()) missing.push('collab.repo');
        if (!gc.private.owner.trim()) missing.push('private.owner');
        if (!gc.private.repo.trim()) missing.push('private.repo');
        if (missing.length) {
          this.editJsonError = `GitHub Collab: required field(s) missing — ${missing.join(', ')}`;
          return;
        }
        const _collaborators = (this.editCollaboratorsText || '')
          .split(/\r?\n/)
          .map(l => l.trim())
          .filter(Boolean)
          .map(l => {
            const idx = l.indexOf(':');
            if (idx > 0) return { handle: l.slice(0, idx).trim(), role: l.slice(idx + 1).trim() };
            return { handle: l };
          });
        payload.GithubCollab = {
          collab: { owner: gc.collab.owner.trim(), repo: gc.collab.repo.trim() },
          private: { owner: gc.private.owner.trim(), repo: gc.private.repo.trim() },
          ...(gc.app.slug.trim() || gc.app.app_id || gc.app.client_id.trim() ? {
            app: {
              ...(gc.app.slug.trim() ? { slug: gc.app.slug.trim() } : {}),
              ...(typeof gc.app.app_id === 'number' && !Number.isNaN(gc.app.app_id) ? { app_id: gc.app.app_id } : {}),
              ...(gc.app.client_id.trim() ? { client_id: gc.app.client_id.trim() } : {}),
            }
          } : {}),
          ...(gc.workflows.sync.trim() || gc.workflows.notify.trim() ? {
            workflows: {
              ...(gc.workflows.sync.trim() ? { sync: gc.workflows.sync.trim() } : {}),
              ...(gc.workflows.notify.trim() ? { notify: gc.workflows.notify.trim() } : {}),
            }
          } : {}),
          ...(_collaborators.length ? { customer_collaborators: _collaborators } : {}),
        };
      } else {
        payload.GithubCollab = null;
      }
      this.editJsonError = null;
    } catch (e: any) {
      this.editJsonError = 'Invalid JSON: ' + (e?.message || 'parse error');
      return;
    }
    const res = await this.scannersService.updateScanner(this.editScanner.Id, payload);
    if (res && !res.err) {
      // IT-F2-391 clar a712d669 — NC settings are now inline on channels[]
      // entries (enabled / body_html_template / schedule / data_source).
      // The generic scanner PUT above persists them in one round-trip;
      // the dedicated /admin/nc/scanner-config/:id second round-trip was
      // retired. f2-session's reader-side mirror (PR #251) keeps legacy
      // channel_templates in sync during the overlap window.
      // Re-fetch from the DB so the grid reflects the persisted state
      // (handles cases where the backend canonicalised or augmented
      // fields, e.g. verified status). In-place mutation didn't work
      // because ag-grid skipped the re-render when the row instance
      // stayed the same.
      await this.get_scanners();
      if (this.grid?.api) this.grid.api.setGridOption('rowData', this.scanners);
      this.showEditScannerModal = false;
      this.showStreamEditor = false;
      this.showChannelsCatalogEditor = false;
    }
  }
  on_delete_scanner_click() {
    this.showDeleteScannerModal = true;
  }
  async delete_scanner(scanner: any) {
    let res = await this.scannersService.deleteScanner(scanner.Id);
    if (res?.success) {
      //Remove user from grid
      _.remove(this.scanners, (s: any) => {
        return s.Id === scanner.Id;
      });
      this.grid.api.setGridOption("rowData", this.scanners);
      this.selected_scanner = null;
      this.showDeleteScannerModal = false;
    }
  }

  async on_scanner_edit(event: any) {
    const node = event.node;
    const oldValue = event.oldValue;
    const newValue = event.newValue;
    const colId = event.column.colId;
    
    if (oldValue === newValue) return;

    switch(colId) {
      case 'Name':
      case 'Client':
      case 'Url':
      case 'AppUrl':
      case 'RoleAccess': {
        let res = await this.scannersService.updateScanner(node.data.Id, node.data);
        if (res?.err) {
          //Revert the change
          node.data[colId] = oldValue;
          node.setData(node.data);
        }
        break;
      }
      case 'notification_center_enabled': {
        // Editor value is 'enabled' | 'disabled'. Translate to the
        // sub-doc shape the backend accepts. Preserve any existing
        // sibling fields on notification_center so a future default_
        // channel / test_recipients edit here doesn't wipe them.
        const on = String(newValue).toLowerCase() === 'enabled';
        const current = (node.data.notification_center && typeof node.data.notification_center === 'object')
          ? { ...node.data.notification_center }
          : {};
        node.data.notification_center = { ...current, enabled: on };
        let res = await this.scannersService.updateScanner(node.data.Id, node.data);
        if (res?.err) {
          node.data.notification_center = current;
          node.setData(node.data);
        }
        break;
      }
      default:
        break;
    }
  }
}
